import { Product } from '@features/catalog/models/product.models';
import {
  ProductListFilters,
  UNCATEGORIZED_CATEGORY_FILTER_ID,
} from '@features/catalog/models/product-list-filters.models';
import { assignedCategoryIdsFromProduct } from '@features/catalog/utils/product-category-assignment.utils';
import {
  PRODUCT_VISIBILITY_CHANNELS,
  type ProductVisibilityChannelConfig,
} from '@features/catalog/utils/product-visibility.channels';

export function normalizeVisibleInChannels(
  filters?: ProductListFilters
): string[] | null {
  const raw = filters?.visibleInChannels ?? filters?.visibilityFilters ?? [];
  const ids = [...new Set(raw.map((id) => String(id).trim()).filter(Boolean))];
  return ids.length ? ids : null;
}

export function splitProductCategoryFilterIds(categoryIds?: string[] | null): {
  realCategoryIds: string[];
  includeUncategorized: boolean;
} {
  const raw = [...new Set((categoryIds ?? []).map((id) => String(id).trim()).filter(Boolean))];
  const includeUncategorized = raw.includes(UNCATEGORIZED_CATEGORY_FILTER_ID);
  const realCategoryIds = raw.filter((id) => id !== UNCATEGORIZED_CATEGORY_FILTER_ID);
  return { realCategoryIds, includeUncategorized };
}

export function productMatchesCategoryFilter(
  product: Product,
  realCategoryIds: string[],
  includeUncategorized: boolean
): boolean {
  if (!includeUncategorized && realCategoryIds.length === 0) {
    return true;
  }
  const assignedIds = assignedCategoryIdsFromProduct(product);
  const inCategory =
    realCategoryIds.length > 0 && realCategoryIds.some((id) => assignedIds.includes(id));
  const isUncategorized = assignedIds.length === 0;
  return inCategory || (includeUncategorized && isUncategorized);
}

/** Variables GraphQL para filtro de categorías en `products(...)`. */
export function graphqlCategoryFilterVariables(categoryIds?: string[] | null): {
  categoryIds: string[] | null;
  includeUncategorized: boolean;
} {
  const { realCategoryIds, includeUncategorized } = splitProductCategoryFilterIds(categoryIds);
  return {
    categoryIds: realCategoryIds.length ? realCategoryIds : null,
    includeUncategorized,
  };
}

function productMatchesVisibilityChannels(product: Product, channelIds: string[]): boolean {
  return channelIds.every((id) => {
    const config: ProductVisibilityChannelConfig | undefined = PRODUCT_VISIBILITY_CHANNELS.find(
      (c) => c.id === id
    );
    if (!config) {
      return false;
    }
    return product[config.productField];
  });
}

function effectiveProductStructure(product: Product): 'simple' | 'variable' {
  return product.productStructure === 'variable' ? 'variable' : 'simple';
}

/**
 * Filtros en cliente cuando el listado no puede delegar al servidor (tests o respuestas parciales).
 */
export function applyProductListClientFilters(
  products: Product[],
  filters?: ProductListFilters
): Product[] {
  if (!filters) {
    return products;
  }
  const { realCategoryIds, includeUncategorized } = splitProductCategoryFilterIds(
    filters.categoryIds
  );
  const visibility = normalizeVisibleInChannels(filters) ?? [];

  return products.filter((p) => {
    if (filters.activeOnly === true && !p.active) {
      return false;
    }
    if (filters.activeOnly === false && p.active) {
      return false;
    }
    if (visibility.length > 0 && !productMatchesVisibilityChannels(p, visibility)) {
      return false;
    }
    if (filters.productStructure && effectiveProductStructure(p) !== filters.productStructure) {
      return false;
    }
    if (!productMatchesCategoryFilter(p, realCategoryIds, includeUncategorized)) {
      return false;
    }
    if (filters.inventoryManaged === true && !p.inventoryManaged) {
      return false;
    }
    if (filters.inventoryManaged === false && p.inventoryManaged) {
      return false;
    }
    return true;
  });
}
